// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/* eslint-disable react-refresh/only-export-components -- Context modules export both Provider and hook by design */
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { logout as authLogout, clearAccessToken } from '../auth/auth-store';
import { clearCache } from '../utils/cache';
import { setUser as setErrorUser } from '../utils/errorReporting';

type ClubSelection = {
  id: string;
  name: string;
};

export type SessionState = {
  username: string;
  role: string;
  roles?: string[]; // All roles assigned to the member
  club: ClubSelection;
  member_id?: string; // Optional: member ID for member-authenticated sessions
};

/**
 * Check if the user has any of the specified roles.
 * Admin role has access to everything.
 */
export function hasAnyRole(session: SessionState | null, requiredRoles: string[]): boolean {
  if (!session?.roles || session.roles.length === 0) {
    // Fall back to single role for backward compatibility
    if (session?.role) {
      return session.role === 'admin' || requiredRoles.includes(session.role);
    }
    return false;
  }
  // Admin role has access to everything
  if (session.roles.includes('admin')) {
    return true;
  }
  // Check if user has any of the required roles
  return requiredRoles.some((role) => session.roles?.includes(role));
}

type SessionContextValue = {
  session: SessionState | null;
  setSession: (_session: SessionState) => void;
  clearSession: () => void;
};

const SESSION_KEY = 'clubview_session';

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

const readSession = (): SessionState | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  let raw = window.localStorage.getItem(SESSION_KEY);
  // Migrate from old key (pre-rebrand)
  if (!raw) {
    raw = window.localStorage.getItem('clubview_session');
    if (raw) {
      window.localStorage.setItem(SESSION_KEY, raw);
      window.localStorage.removeItem('clubview_session');
    }
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionState;
  } catch {
    return null;
  }
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<SessionState | null>(() => readSession());

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      setSession: (_state: SessionState) => {
        setSessionState(_state);
        setErrorUser(_state.member_id ?? null, _state.club.id);
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(SESSION_KEY, JSON.stringify(_state));
          window.localStorage.setItem('clubview_selected_role', _state.role);
          window.localStorage.setItem('clubview_selected_club', _state.club.id);
          window.localStorage.setItem('clubview_selected_club_name', _state.club.name);
        }
      },
      clearSession: () => {
        setSessionState(null);
        setErrorUser(null);

        // Clear memory token via auth-store
        clearAccessToken();

        // Clear API response cache to prevent cross-club data leakage (W43)
        clearCache();

        // Clear session and legacy tokens from localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem(SESSION_KEY);
          window.localStorage.removeItem('clubview_selected_role');
          window.localStorage.removeItem('clubview_selected_club');
          window.localStorage.removeItem('clubview_selected_club_name');
          // Legacy token cleanup (no longer used but may exist)
          window.localStorage.removeItem('clubview_token');
          window.localStorage.removeItem('clubview_token_expires');
          window.localStorage.removeItem('clubview_club_token');
        }

        // Call server logout to clear httpOnly cookie (async, fire and forget)
        authLogout().catch(() => {
          // Ignore logout errors - local state is already cleared
        });
      }
    }),
    [session]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return ctx;
}

/**
 * useViewerRole returns the current viewer's role, or '' if there is no
 * SessionProvider in scope (e.g. in unit tests that render a component in
 * isolation). Use this instead of `useSession().session?.role` when the
 * caller only needs the role and shouldn't blow up under stripped-down
 * test harnesses.
 */
export function useViewerRole(): string {
  const ctx = useContext(SessionContext);
  return ctx?.session?.role ?? '';
}
